import React from 'react';
import PropTypes from 'prop-types';

import { formatHours } from './redistributionHelpers';

const BADGE_FACE_BY_VARIANT = {
  REG: 'ttg-redistribute-summary-badge-reg',
  OT: 'ttg-redistribute-summary-badge-ot',
  DT: 'ttg-redistribute-summary-badge-dt',
  neutral: 'ttg-redistribute-summary-badge-neutral',
};

const BADGE_TEXT_BY_VARIANT = {
  REG: 'ttg-redistribute-summary-badge-reg-text',
  OT: 'ttg-redistribute-summary-badge-ot-text',
  DT: 'ttg-redistribute-summary-badge-dt-text',
  neutral: 'ttg-redistribute-summary-badge-neutral-text',
};

const COST_CODE_VALUE_CLASS_BY_VARIANT = {
  REG: 'ttg-redistribute-cost-code-coded-value-reg',
  OT: 'ttg-redistribute-cost-code-coded-value-ot',
  DT: 'ttg-redistribute-cost-code-coded-value-dt',
  neutral: 'ttg-redistribute-summary-hour-value-neutral',
};

const SUMMARY_VALUE_CLASS_BY_VARIANT = {
  default: 'ttg-redistribute-summary-hour-value-default',
  complete: 'ttg-redistribute-summary-hour-value-complete',
  overrun: 'ttg-redistribute-summary-hour-value-overrun',
  costCoded: 'ttg-redistribute-summary-hour-value-cost-coded',
  neutral: 'ttg-redistribute-summary-hour-value-neutral',
};

function resolveValueClassName(valueStylePreset, variant) {
  if (!valueStylePreset) return null;
  if (valueStylePreset === 'costCodeInline') {
    return COST_CODE_VALUE_CLASS_BY_VARIANT[variant]
      || COST_CODE_VALUE_CLASS_BY_VARIANT.neutral;
  }
  if (valueStylePreset === 'footerTotal') return 'ttg-redistribute-footer-total-value';
  if (SUMMARY_VALUE_CLASS_BY_VARIANT[valueStylePreset]) {
    return SUMMARY_VALUE_CLASS_BY_VARIANT[valueStylePreset];
  }
  return null;
}

export default function RedistributeHourBadge({
  variant,
  label,
  value,
  valueStylePreset,
  valueClassName,
  decimals,
  containerClassName,
}) {
  const faceClassName = BADGE_FACE_BY_VARIANT[variant] || BADGE_FACE_BY_VARIANT.neutral;
  const textClassName = BADGE_TEXT_BY_VARIANT[variant] || BADGE_TEXT_BY_VARIANT.neutral;
  const displayLabel = label ?? (variant === 'neutral' ? 'HRS' : variant);

  const showValue = value !== undefined && value !== null;
  const resolvedValueClassName = valueClassName
    || resolveValueClassName(valueStylePreset, variant);

  const badge = (
    <div className={`ttg-redistribute-summary-badge-face ${faceClassName}`}>
      <span className={textClassName}>{displayLabel}</span>
    </div>
  );

  if (!showValue) {
    return containerClassName ? <div className={containerClassName}>{badge}</div> : badge;
  }

  return (
    <div className={['ttg-redistribute-summary-badge-group', containerClassName].filter(Boolean).join(' ')}>
      {badge}
      <span className={resolvedValueClassName}>
        {formatHours(value, decimals)}
      </span>
    </div>
  );
}

RedistributeHourBadge.propTypes = {
  variant: PropTypes.oneOf(['REG', 'OT', 'DT', 'neutral']).isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueStylePreset: PropTypes.oneOf([
    'costCodeInline',
    'footerTotal',
    'unassignedFamily',
    'costCoded',
    'neutral',
    'default',
    'complete',
    'overrun',
  ]),
  valueClassName: PropTypes.string,
  decimals: PropTypes.number,
  containerClassName: PropTypes.string,
};

RedistributeHourBadge.defaultProps = {
  label: undefined,
  value: undefined,
  valueStylePreset: undefined,
  valueClassName: undefined,
  decimals: 2,
  containerClassName: undefined,
};
